'use client';

import { Tab, Tabs } from '@nextui-org/react';
import dynamic from 'next/dynamic';
import { useRouter, useSearchParams } from 'next/navigation';
import React, { useEffect, useState } from 'react';

import RenderWhen from '@/components/RenderWhen';
import useWindowSize from '@/hooks/useWindowSize';

import type { DataGraphHistory, DataStatistic } from '../page';
import FarmingChart from './chart';
import { convertGraphData } from './helpers';
import InfoFarming from './info';
import LockContent from './lock';

const DynamicFarmingTable = dynamic(() => import('./table'));
const DynamicFarmingTableMobile = dynamic(() => import('./table/TableMobile'));
const DynamicFarmingTableTablet = dynamic(() => import('./table/TableTablet'));

export enum KeyCategoryFarming {
  Stake = 'stake',
  Lock = 'lock',
}

const FarmingPage: React.FC<{
  apr: any;
  statistic: DataStatistic;
  graphData: DataGraphHistory[];
}> = ({ apr, statistic, graphData }) => {
  const searchParam = useSearchParams();
  const router = useRouter();
  let dataChart = convertGraphData(graphData);
  const { isIpadView, isMobileView, isMobileHorizontalView } = useWindowSize();
  dataChart = dataChart.filter((i: any) => i[0] > Date.now() - 24 * 3600 * 1000);

  const [keySelected, setKeySelected] = useState(
    searchParam?.get('category') || KeyCategoryFarming.Stake,
  );

  useEffect(() => {
    router.replace(`/farming?category=${keySelected}`, { scroll: false });
  }, [keySelected]);

  return (
    <div className="py-10">
      <div className="main-container">
        <h1 className="mb-6 text-[28px] font-semibold text-black lg:text-[22px] sm:!text-[18px]">
          Total liquidity
        </h1>
        <div className="-mx-4 flex flex-wrap">
          <div className="w-[65%] px-4 lg:w-full">
            <div className="min-h-[350px] rounded-[24px] border-1 border-gray-100 px-2 py-5 sm:min-h-[300px] sm:px-0 sm:py-3">
              <FarmingChart dataChart={dataChart} />
            </div>
          </div>
          <div className="w-[35%] px-4 lg:mt-4 lg:w-full">
            <InfoFarming
              apr={apr['0x7e2A35C746F2f7C240B664F1Da4DD100141AE71F'.toLowerCase()]}
              statistic={statistic}
            />
          </div>
        </div>
        <div className="mt-10">
          <Tabs
            selectedKey={keySelected}
            onSelectionChange={(key) => setKeySelected(key as string)}
            classNames={{
              tabList: 'bg-gray-50 rounded-[100px] p-[6px] w-[600px] xl:w-[300px] md:w-full',
              tabContent: 'font-semibold',
              cursor: 'rounded-[100px] shadow-none bg-white',
              base: 'w-full block mb-[-58px] md:mb-0',
              tab: 'h-9',
            }}
          >
            <Tab key={KeyCategoryFarming.Stake} title="Legacy Stake">
              <RenderWhen>
                <RenderWhen.If isTrue={isIpadView || isMobileHorizontalView}>
                  <DynamicFarmingTableTablet apr={apr} />
                </RenderWhen.If>
                <RenderWhen.If isTrue={!isIpadView && !isMobileView && !isMobileHorizontalView}>
                  <DynamicFarmingTable apr={apr} />
                </RenderWhen.If>
                <RenderWhen.If isTrue={isMobileView}>
                  <DynamicFarmingTableMobile apr={apr} />
                </RenderWhen.If>
              </RenderWhen>
            </Tab>
            <Tab key={KeyCategoryFarming.Lock} title="Lock Stake">
              <LockContent />
            </Tab>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default FarmingPage;
